import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useValidation } from '../hooks';
import type { PendingItem } from '../api';

interface Props {
  clientId: string;
  compact?: boolean;
}

/**
 * Painel de completude + pendências (SPEC 02 §1 — RN-COM-055,
 * RN-COM-056). Reflete em tempo real o estado do cliente:
 *
 *  - Barra de completude (percentual), colorida por faixa;
 *  - Lista de pendências que impedem ativação, cada item clicável
 *    (`data-field` mapeia para a aba/campo onde o usuário resolve).
 *
 * O componente é apenas leitura — o botão "Ativar Cliente" fica na
 * seção de ciclo de vida e usa o mesmo endpoint /validation para
 * decidir se deve tentar a transição ou mostrar as pendências primeiro.
 *
 * `compact` reduz a área ocupada (útil no cabeçalho).
 */
export const ClienteValidacao: React.FC<Props> = ({ clientId, compact = false }) => {
  const { data, isLoading } = useValidation(clientId);

  if (isLoading || !data) {
    return (
      <div className={compact ? 'text-xs text-gray-500' : 'text-sm text-gray-500'}>
        Verificando completude…
      </div>
    );
  }

  const { percent, pendingItems, canActivate } = data;

  return (
    <div className={compact ? 'space-y-2' : 'space-y-4'} data-testid="cliente-validacao">
      <ProgressBar percent={percent} compact={compact} />

      {canActivate ? (
        <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
          <span>Cadastro completo — cliente pode ser ativado.</span>
        </div>
      ) : (
        <PendingList pendingItems={pendingItems} />
      )}
    </div>
  );
};

const ProgressBar: React.FC<{ percent: number; compact: boolean }> = ({ percent, compact }) => {
  const color =
    percent >= 90 ? 'bg-green-500' : percent >= 60 ? 'bg-blue-500' : 'bg-yellow-500';
  return (
    <div>
      <div className={compact ? 'mb-1 flex items-center justify-between text-xs' : 'mb-1 flex items-center justify-between text-sm'}>
        <span className="font-medium text-gray-700">Cadastro {percent}% completo</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className={`h-full transition-all duration-300 ${color}`}
          style={{ width: `${Math.max(percent, 3)}%` }}
          data-testid="completude-bar"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
};

/**
 * Mapeia o `field` do PendingItem para o elemento âncora na página.
 * O componente cria um `id` estável no formato `pendencia-<field>` que
 * as abas registram (via `data-pending-anchor`). O clique faz scroll
 * suave e aplica um "flash" temporário no destino via classe CSS.
 */
const PendingList: React.FC<{ pendingItems: PendingItem[] }> = ({ pendingItems }) => {
  const handleClick = (field: string) => {
    const target = document.querySelector<HTMLElement>(`[data-pending-anchor="${field}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('ring-2', 'ring-yellow-400', 'transition-shadow');
    setTimeout(() => {
      target.classList.remove('ring-2', 'ring-yellow-400');
    }, 1800);
  };

  return (
    <div
      className="rounded-md border border-yellow-200 bg-yellow-50 p-3"
      data-testid="pendencias-panel"
    >
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-yellow-900">
        <AlertTriangle size={16} />
        <span>
          {pendingItems.length} {pendingItems.length === 1 ? 'pendência' : 'pendências'} impedem a
          ativação
        </span>
      </div>
      <ul className="space-y-1 text-sm text-yellow-900">
        {pendingItems.map((p) => (
          <li key={p.code}>
            <button
              type="button"
              onClick={() => handleClick(p.field)}
              className="text-left hover:underline"
              data-testid={`pendencia-${p.code}`}
              data-field={p.field}
            >
              • {p.message}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
